import { useState, useEffect, useRef } from 'react';
import { StageMappingHookResult } from '../types/stageTypes';
import { useStageData } from './useStageData';
import { useStageGroups } from './useStageGroups';
import { useStageSelection } from './useStageSelection';
import { useStageDragDrop } from './useStageDragDrop';
import { useStageModification } from './useStageModification';
import { useStagePersistence } from './useStagePersistence';

/**
 * Main hook that composes all stage mapping functionality
 * @returns All state and functions for the stage mapping UI
 */
export function useStageMapping(): StageMappingHookResult {
    // Load stage data
    const {
        loading,
        saving,
        originalStages,
        atsConnected,
        noJobsConnected,
        setSavingState,
        fetchData
    } = useStageData();

    // Track changes to originalStages with a ref to debug
    const originalStagesRef = useRef(originalStages);

    // Track if originalStages have changed
    useEffect(() => {
        originalStagesRef.current = originalStages;
    }, [originalStages]);

    // State to hold modified original stages
    const [modifiedOriginalStages, setModifiedOriginalStages] = useState(originalStages);

    // Update modified stages when original stages change
    useEffect(() => {
        if (originalStages.length > 0) {
            setModifiedOriginalStages(originalStages);
        }
    }, [originalStages]);

    // Manage stage groups
    const {
        stageGroups,
        filteredGroups,
        searchQuery,
        setSearchQuery,
        toggleGroupExpansion,
        setGroups
    } = useStageGroups(modifiedOriginalStages);

    // Manage stage selection
    const {
        selectedStages,
        selectedCount,
        toggleStageSelection,
        clearSelection
    } = useStageSelection();

    // Handle drag and drop
    const { handleDragEnd } = useStageDragDrop(
        stageGroups,
        modifiedOriginalStages,
        setModifiedOriginalStages,
        setGroups,
        clearSelection
    );

    // Handle stage modifications
    const {
        applyStageTypeToSelected,
        toggleAlwaysUnmap,
        addedCount,
        updatedCount,
        unmappedCount
    } = useStageModification(
        stageGroups,
        modifiedOriginalStages,
        selectedStages,
        setModifiedOriginalStages,
        setGroups,
        clearSelection
    );

    // Handle saving and resetting
    const {
        handleSaveConfirm,
        handleResetChanges
    } = useStagePersistence(
        stageGroups,
        originalStages,
        setModifiedOriginalStages,
        setGroups,
        clearSelection,
        setSavingState,
        fetchData
    );

    // Return all required state and functions
    return {
        loading,
        saving,
        atsConnected,
        noJobsConnected,
        originalStages: modifiedOriginalStages,
        stageGroups,
        filteredGroups,
        searchQuery,
        selectedStages,
        selectedCount,
        addedCount,
        updatedCount,
        unmappedCount,
        setSearchQuery,
        toggleGroupExpansion,
        toggleStageSelection,
        handleDragEnd,
        applyStageTypeToSelected,
        handleSaveConfirm,
        handleResetChanges,
        clearSelection,
        toggleAlwaysUnmap
    };
} 